import type en from '../en/common'
import type { Translation } from '../types'

/** Words and formats used across the dashboard */
const common: Translation<typeof en> = {
  "common.justNow": "zojuist",
  "common.minutesAgo": "{count} min. geleden",
  "common.hoursAgo": "{count} u. geleden",
  "common.daysAgo": "{count} d. geleden",
  "common.duration.minutes": { one: "{count} minuut", other: "{count} minuten" },
  "common.duration.hours": { one: "{count} uur", other: "{count} uur" },
  "common.duration.days": { one: "{count} dag", other: "{count} dagen" },
  "common.duration.weeks": { one: "{count} week", other: "{count} weken" },
  "common.cancel": "Annuleren",
  "common.confirm": "Bevestigen",
  "common.continue": "Doorgaan",
  "common.working": "Bezig",
  "common.loading": "Laden…",
  "common.tryAgain": "Dat is niet gelukt — probeer het opnieuw",
  "common.tryAgainButton": "Opnieuw proberen",
  "common.retry": "Opnieuw",
  "common.copy": "Kopiëren",
  "common.download": "Downloaden",
  "common.saved": "Opgeslagen",
  "common.notSaved": "Niet opgeslagen",
  "common.couldNotCopy": "Kopiëren is niet gelukt",
  "common.goBack": "Terug",
  "common.password": "Wachtwoord",
  "common.emailAddress": "E-mailadres",
  "common.showPassword": "Wachtwoord tonen",
  "common.hidePassword": "Wachtwoord verbergen",
  "common.signOut": "Uitloggen",
  "common.notFoundTitle": "Pagina niet gevonden",
  "common.notFoundText": "De pagina die u zoekt bestaat niet of u hebt er geen toegang toe.",
  "common.errorTitle": "Er is iets misgegaan",
  "common.errorUnexpected": "Er is een onverwachte fout opgetreden.",
  "common.loadFailed": "Kon de gegevens niet laden.",
  "common.showing": "{start}–{end} van {total}",
  "common.previousPage": "Vorige pagina",
  "common.nextPage": "Volgende pagina",
  "common.notifications": "Notificaties",
  "common.dismiss": "Sluiten",
  "common.refresh": "Vernieuwen",
  "common.viewAll": "Alles bekijken",
  "common.view": "Bekijken",
  "common.delete": "Verwijderen",
  "common.close": "Sluiten",
  "common.actions": "Acties",
  "common.status": "Status",
  "common.name": "Naam",
  "common.description": "Omschrijving",
  "common.clients": "Clients",
  "common.devices": "Apparaten",
  "common.active": "Actief",
  "common.disabled": "Uitgeschakeld",
  "common.saveFailed": "Opslaan mislukt",
  "common.internalError": "Interne serverfout",
  "common.clientsCount": { one: "{count} client", other: "{count} clients" },
  "common.devicesCount": { one: "{count} apparaat", other: "{count} apparaten" },
  "common.onlineCount": "{count} online",
  "common.critical": { one: "{count} kritiek", other: "{count} kritiek" },
  "common.status.ONLINE": "ONLINE",
  "common.status.OFFLINE": "OFFLINE",
  "common.status.WARNING": "WAARSCHUWING",
  "common.status.ERROR": "FOUT",
  "common.status.PENDING": "IN AFWACHTING",
  "common.status.PENDING_VPN": "WACHT OP VPN",
  "common.status.PROVISIONING": "WORDT INGERICHT",
  "common.status.ACTIVE": "ACTIEF",
  "common.status.INACTIVE": "INACTIEF",
  "common.status.COMPLETED": "VOLTOOID",
  "common.status.RUNNING": "BEZIG",
  "common.status.FAILED": "MISLUKT",
  "common.status.EXPIRED": "VERLOPEN",
  "common.status.SUSPENDED": "OPGESCHORT",
  "common.status.DELETED": "VERWIJDERD",
  "common.status.FAILOVER": "FAILOVER",
  "common.status.APPLIED": "TOEGEPAST",
  "common.status.UNSUPPORTED": "NIET ONDERSTEUND",
  "common.select": "Kies…",
  "common.selectAll": "Alles selecteren",
  "common.deselectAll": "Alles deselecteren",
  "common.more": "+{count} meer",
  "common.optional": "(optioneel)",
  "common.sitesCount": { one: "{count} locatie", other: "{count} locaties" },
  "common.send": "Versturen",
  "common.uptime.days": "{d}d {h}u",
  "common.uptime.hours": "{h}u {m}m",
  "common.uptime.minutes": "{m}m",
  "common.copied": "Gekopieerd",
  "common.remove": "Verwijderen",
  "common.saving": "Opslaan…",
  "common.username": "Gebruikersnaam",
  "common.tryAgainMoment": "Probeer het zo meteen opnieuw.",
  "common.duration.seconds": { one: "{count} seconde", other: "{count} seconden" },
  "common.status.PENDING_SETUP": "WORDT INGERICHT",
  "common.status.CANCELLED": "OPGEZEGD",
  "common.languageName.en": "Engels",
  "common.languageName.nl": "Nederlands",
  "common.languageName.fr": "Frans",
  "common.languageName.de": "Duits",
  "common.languageName.es": "Spaans",
  "common.status.QUEUED": "BEWAARD VOOR LATER",
  "common.status.DISCARDED": "VERWORPEN",
  "common.renewCert.title": "Het VPN-certificaat van {name} vernieuwen?",
  "common.renewCert.titleMine": "Je VPN-certificaat vernieuwen?",
  "common.renewCert.stops": "Het huidige certificaat werkt meteen niet meer: er is geen VPN-verbinding tot het nieuwe bestand is geïnstalleerd.",
  "common.renewCert.remote": "Een unit of controller die alleen via de VPN bereikbaar is, is dan niet meer van hieruit te bereiken: voor het nieuwe bestand kan iemand ter plaatse nodig zijn.",
  "common.renewCert.noWayBack": "Dit kan niet ongedaan worden gemaakt: het oude certificaat kan niet meer worden gebruikt.",
  "common.renewCert.continue": "Begrepen, doorgaan",
  "common.renewCert.last": "Het certificaat nu vernieuwen? Het huidige werkt niet meer zodra je klikt.",
  "common.renewCert.renewNow": "Nu vernieuwen",
  "common.export.button": "Exporteren",
  "common.export.title": "Exporteren",
  "common.export.what": "Wat erin komt",
  "common.export.summaryNone": "Alles wat getoond wordt",
  "common.export.rows": "Rijen",
  "common.export.thisPage": "Deze pagina ({n})",
  "common.export.allMatches": "Alle resultaten ({n})",
  "common.export.tooMany": "Meer dan 10 000 rijen: exporteer deze pagina, of verfijn de filters",
  "common.export.parts": "Onderdelen",
  "common.export.format": "Formaat",
  "common.export.xlsx": "Spreadsheet (.xlsx)",
  "common.export.csv": "Spreadsheet (CSV)",
  "common.export.pdf": "PDF-rapport",
  "common.export.md": "Markdown-opdracht voor Claude Code",
  "common.export.go": "Exporteren",
  "common.export.working": "Exporteren…",
  "common.export.failed": "Het exporteren is niet gelukt",
  "common.export.search": "zoeken op “{q}”",
  "common.export.sortedBy": "gesorteerd op {column}",
  "common.export.sortedByDesc": "gesorteerd op {column}, aflopend",
  "common.export.field": "Veld",
  "common.export.value": "Waarde",
  "common.export.deviceInfo": "Apparaatgegevens",
  "common.export.siteInfo": "Locatiegegevens",
  "common.export.traffic": "Verkeer",
  "common.export.cpu": "CPU",
  "common.export.serial": "Serienummer",
  "common.export.mac": "MAC-adres",
  "common.export.level": "Niveau",
  "common.export.title_": "Titel",
  "common.export.message": "Bericht",
  "common.export.time": "Tijd",
  "common.export.security": "Beveiliging",
  "common.export.band": "Band",
  "common.export.vlan": "VLAN",
  "common.export.ssid": "SSID",
  "common.export.enabled": "Ingeschakeld",
  "common.export.clientCount": "Clients",
  "common.export.period": "periode {period}",
  "common.export.range": "{from} tot {to}",
  "common.export.radios": "Radio’s",
  "common.export.modem": "Modem",
  "common.export.operator": "Provider",
  "common.export.network": "Netwerktype",
  "common.export.events": "Gebeurtenissen",
  "common.export.controller": "Controller",
  "common.export.wired": "Bekabeld",
  "common.export.eventInfo": "Evenementgegevens",
  "common.export.online": "Online en offline",
  "common.export.loads": "Uit en terug",
  "common.export.customer": "Klant",
  "common.export.resolvedAt": "Opgelost",
  "common.export.loadedAt": "Geladen",
  "common.export.loadedBy": "Geladen door",
  "common.export.returnedAt": "Teruggekomen",
  "common.export.returnedBy": "Teruggebracht door",
  "common.export.figures": "Cijfers",
  "common.export.critical": "Kritiek",
  "common.export.vlans": "VLAN's",
  "common.export.wifi": "Wifi",
  "common.export.kind": "Soort",
  "common.export.changedBy": "Gewijzigd door",
  "common.export.changedAt": "Gewijzigd op",
  "common.export.change": "Wijziging",
  "common.export.articleInfo": "Artikelgegevens",
  "common.export.when": "Wanneer",
  "common.export.copied": "Gekopieerd en gedownload",
  "common.export.downloadedOnly": "Gedownload (kopiëren is hier niet toegestaan)",
  "common.export.twoFactor": "Tweestapsaanmelding",
  "common.export.systemRole": "Systeemrol",
  "common.export.type": "Type",
  "common.export.deviceFamily": "Apparaatfamilie",
  "common.export.origin": "Herkomst",
  "common.export.isDefault": "Standaard",
  "common.export.madeBy": "Gemaakt door",
  "common.export.created": "Aangemaakt",
  "common.export.applied": "Keren toegepast",
  "common.export.watches": "Bewaakt",
  "common.export.channels": "Kanalen",
  "common.export.mutedDevices": "Gedempte apparaten",
  "common.export.who": "Wie",
  "common.export.action": "Actie",
  "common.export.resource": "Onderdeel",
  "common.export.health": "Status",
  "common.export.goesTo": "Gaat naar",
  "common.export.checkedAt": "Gecontroleerd",
  "common.export.times": "Keren gezien",
  "common.export.exampleSite": "Voorbeeldlocatie",
  "common.export.noLimit": "Geen limiet",
  "common.export.lastSeen": "Laatst gezien",
  "common.export.simSlot": "Simsleuf",
  "common.export.pinTries": "Resterende PIN-pogingen",
  "common.export.pukTries": "Resterende PUK-pogingen",
  "common.export.pinUsed": "Gebruikte PIN",
  "common.export.storedPin": "Opgeslagen PIN van de router klopt",
  "common.export.readAt": "Gelezen op",
  "common.export.sims": "Simsleuven",
  "common.export.latency": "Vertraging (ms)",
  "common.export.loss": "Verlies (%)",
  "common.export.peer": "Tegenpartij",
  "common.export.signal": "{name} (dBm)",
}

export default common
